import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaUpload,
  FaFileExcel,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaEdit,
  FaBan,
  FaPause,
  FaPlay,
  FaSearch,
  FaDraftingCompass,
  FaPaperPlane,
  FaUserEdit,
  FaUserCheck,
} from "react-icons/fa";
import {
  FiFilter,
  FiX,
  FiCalendar,
  FiSettings,
  FiSearch,
  FiRefreshCw,
  FiUsers,
  FiCreditCard,
} from "react-icons/fi";
import { LuEye } from "react-icons/lu";
import api from "../../libs/axios";
import { useAuth } from "../../provider/auth.provider";
import { useConfirm } from "../../provider/confirm.provider";
import { useDebouncedCallback } from "../../hooks/useDebounce";
import { Pagination, PaginationType } from "../../utils/pagination.utils";
import { ExcelDownload } from "../../utils/excel.utils";
import {
  MAX_DATE_FILTER_RANGE_DAYS,
  dateRangeInputBounds,
  firstDateRangeError,
  type DateRangeTriple,
} from "../../utils/date-range.utils";
import { SelectWithSearch, type SelectOption } from "../../utils/select-with-search";
import { designProjectParams } from "./design_site_form";
import {
  DESIGN_ACTIONS,
  DESIGN_BILLING_STATUSES,
  DESIGN_PRIORITIES,
  DESIGN_REPORT_TYPES,
  DESIGN_RESOURCE,
  DESIGN_STAGES,
  DESIGN_STAGE_DATE_COLUMNS,
  DESIGN_VENDOR_STATUSES,
  DESIGN_WORK_STATUSES,
  billingStatusLabel,
  formatAmount,
  formatDateOnly,
  formatDateTime,
  personName,
  priorityBadgeClass,
  priorityLabel,
  reportTypeLabel,
  stageBadgeClass,
  stageLabel,
  vendorStatusLabel,
  workStatusBadgeClass,
  workStatusLabel,
  type Option,
  reportMakerStatusLabel,
  teamProgress,
} from "./design_constants";

type Scope = "all" | "my";

// ── Filters ─────────────────────────────────────────────────────────────────
// Keys are the query params of GET /designing/sites[/all], sent as they are.
export type DesignFilters = Record<string, string | undefined>;

const YES_NO: Option[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

// The ten from/to ranges the list accepts (`<param>_from` / `<param>_to`). Each
// may span at most 62 days — checked here on Apply and again by the server.
const DATE_RANGES: Array<{ param: string; label: string }> = [
  ...DESIGN_STAGE_DATE_COLUMNS.map((c) => ({ param: c.filter, label: c.label })),
  { param: "updated", label: "Last Updated Date" },
  { param: "required", label: "Required Date" },
  { param: "po_date", label: "PO Date" },
];

const FILTER_LABELS: Record<string, string> = {
  site_code: "Site ID",
  site_name: "Site Name",
  reference_id: "Reference ID",
  po_number: "PO Number",
  project_id: "Project",
  activity_id: "Activity",
  client_id: "Client",
  report_type: "Report Type",
  is_billable: "Billable",
  priority: "Priority",
  status: "Work Completion Status",
  workflow_status: "Site Stage",
  report_maker_id: "Report Maker",
  validator_id: "Validator",
  has_report_maker: "Has Report Maker",
  has_validator: "Has Validator",
  billing_status: "Billing Status",
  vendor_status: "Vendor Status",
};
DATE_RANGES.forEach((r) => {
  FILTER_LABELS[`${r.param}_from`] = `${r.label} From`;
  FILTER_LABELS[`${r.param}_to`] = `${r.label} To`;
});

// Filters whose value is one of a fixed list — the chip shows the option label.
const FILTER_OPTIONS: Record<string, Option[]> = {
  report_type: DESIGN_REPORT_TYPES,
  is_billable: YES_NO,
  priority: DESIGN_PRIORITIES,
  status: DESIGN_WORK_STATUSES,
  workflow_status: DESIGN_STAGES,
  has_report_maker: YES_NO,
  has_validator: YES_NO,
  billing_status: DESIGN_BILLING_STATUSES,
  vendor_status: DESIGN_VENDOR_STATUSES,
};

const filterValueLabel = (key: string, value: string, labels: Record<string, string>): string => {
  if (FILTER_OPTIONS[key]) return FILTER_OPTIONS[key].find((o) => o.value === value)?.label || value;
  if (/_(from|to)$/.test(key)) return formatDateOnly(value);
  return labels[key] || value;
};

// "First Last (employee_code)" — module-level so the dropdown's fetch stays stable.
const userOptionLabel = (u: any): string => {
  const name = personName(u);
  return u?.employee_code ? `${name} (${u.employee_code})` : name;
};

const inputClass =
  "w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";

// What the server lets the caller see: DesignSite.view_billing / view_vendor.
interface CommercialAccess {
  billing: boolean;
  vendor: boolean;
}

const accessFromBody = (body: any): CommercialAccess => ({
  billing: !!body?.can_view_billing,
  vendor: !!body?.can_view_vendor,
});

interface FilterModalProps {
  isOpen: boolean;
  scope: Scope;
  commercialAccess: CommercialAccess;
  projectParams: Record<string, string | boolean>;
  initial: DesignFilters;
  initialLabels: Record<string, string>;
  onClose: () => void;
  onApply: (filters: DesignFilters, labels: Record<string, string>) => void;
  onReset: () => void;
}

const FILTER_TABS = [
  { id: "site", label: "Site", icon: FiSearch },
  { id: "stages", label: "Stages", icon: FiSettings },
  { id: "people", label: "People", icon: FiUsers },
  { id: "dates", label: "Dates", icon: FiCalendar },
  { id: "commercials", label: "Commercials", icon: FiCreditCard },
];

const DesignFilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  scope,
  commercialAccess,
  projectParams,
  initial,
  initialLabels,
  onClose,
  onApply,
  onReset,
}) => {
  const [activeTab, setActiveTab] = useState("site");
  const [draft, setDraft] = useState<DesignFilters>(initial);
  const [labels, setLabels] = useState<Record<string, string>>(initialLabels);

  useEffect(() => {
    if (isOpen) {
      setDraft(initial);
      setLabels(initialLabels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // Billing filters only for DesignSite.view_billing, vendor filters only for view_vendor.
  const tabs = FILTER_TABS.filter((t) => t.id !== "commercials" || commercialAccess.billing || commercialAccess.vendor);
  const tab = tabs.some((t) => t.id === activeTab) ? activeTab : "site";

  const set = (key: string, value: string) => setDraft((prev) => ({ ...prev, [key]: value || undefined }));

  // Dropdowns backed by an endpoint keep the picked label for the filter chip.
  const optOf = (key: string): SelectOption | null =>
    draft[key] ? { id: Number(draft[key]), value: draft[key] as string, label: labels[key] || String(draft[key]) } : null;
  const pick = (key: string, opt: SelectOption | null) => {
    set(key, opt ? String(opt.value) : "");
    setLabels((p) => ({ ...p, [key]: opt?.label || "" }));
  };

  const textField = (key: string, placeholder: string) => (
    <div>
      <label className={labelClass}>{FILTER_LABELS[key]}</label>
      <input type="text" className={inputClass} placeholder={placeholder} value={draft[key] || ""} onChange={(e) => set(key, e.target.value)} />
    </div>
  );

  const selectField = (key: string) => (
    <div>
      <label className={labelClass}>{FILTER_LABELS[key]}</label>
      <select className={inputClass} value={draft[key] || ""} onChange={(e) => set(key, e.target.value)}>
        <option value="">All</option>
        {FILTER_OPTIONS[key].map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-5xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" /> Filters
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                  tab === t.id
                    ? "border-blue-500 text-blue-600 bg-white"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {tab === "site" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {textField("site_code", "Site ID contains...")}
              {textField("site_name", "Site name contains...")}
              {textField("reference_id", "Reference ID contains...")}
              {textField("po_number", "PO number contains...")}
              <div>
                <label className={labelClass}>Project</label>
                <SelectWithSearch
                  placeholder="Select project..."
                  apiEndpoint="/projects/list"
                  dataKey="projects"
                  labelKey="project_name"
                  extraParams={projectParams}
                  minSearchLength={0}
                  value={optOf("project_id")}
                  onChange={(opt) => pick("project_id", opt)}
                />
              </div>
              <div>
                <label className={labelClass}>Activity</label>
                {/* From the sites this user can see, not the activity master: a
                    filter never offers a value the table could not return. */}
                <SelectWithSearch
                  placeholder="Select activity..."
                  apiEndpoint="/designing/filter-options"
                  dataKey="options"
                  labelKey="name"
                  extraParams={{ kind: "activity", scope }}
                  minSearchLength={0}
                  value={optOf("activity_id")}
                  onChange={(opt) => pick("activity_id", opt)}
                />
              </div>
              <div>
                <label className={labelClass}>Client</label>
                <SelectWithSearch
                  placeholder="Select client..."
                  apiEndpoint="/designing/filter-options"
                  dataKey="options"
                  labelKey="name"
                  extraParams={{ kind: "client", scope }}
                  minSearchLength={0}
                  value={optOf("client_id")}
                  onChange={(opt) => pick("client_id", opt)}
                />
              </div>
              {selectField("report_type")}
              {selectField("is_billable")}
              {selectField("priority")}
            </div>
          )}

          {tab === "stages" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectField("status")}
              {selectField("workflow_status")}
            </div>
          )}

          {tab === "people" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Report Maker</label>
                <SelectWithSearch
                  placeholder="Select report maker..."
                  apiEndpoint="/designing/filter-users"
                  dataKey="users"
                  extraParams={{ kind: "report_maker", scope }}
                  displayFormat={userOptionLabel}
                  minSearchLength={0}
                  value={optOf("report_maker_id")}
                  onChange={(opt) => pick("report_maker_id", opt)}
                />
              </div>
              <div>
                <label className={labelClass}>Validator</label>
                <SelectWithSearch
                  placeholder="Select validator..."
                  apiEndpoint="/designing/filter-users"
                  dataKey="users"
                  extraParams={{ kind: "validator", scope }}
                  displayFormat={userOptionLabel}
                  minSearchLength={0}
                  value={optOf("validator_id")}
                  onChange={(opt) => pick("validator_id", opt)}
                />
              </div>
              {selectField("has_report_maker")}
              {selectField("has_validator")}
            </div>
          )}

          {tab === "dates" && (
            <>
              <p className="text-xs text-gray-500 mb-4">
                The two dates of a range can be at most {MAX_DATE_FILTER_RANGE_DAYS} days apart.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {DATE_RANGES.map((r) => {
                  const fromKey = `${r.param}_from`;
                  const toKey = `${r.param}_to`;
                  const bounds = dateRangeInputBounds(draft[fromKey], draft[toKey]);
                  return (
                    <div key={r.param}>
                      <label className={labelClass}>{r.label}</label>
                      <div className="grid grid-cols-2 gap-4">
                        <input type="date" className={inputClass} value={draft[fromKey] || ""} onChange={(e) => set(fromKey, e.target.value)} {...bounds.from} />
                        <input type="date" className={inputClass} value={draft[toKey] || ""} onChange={(e) => set(toKey, e.target.value)} {...bounds.to} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {tab === "commercials" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {commercialAccess.billing && selectField("billing_status")}
              {commercialAccess.vendor && selectField("vendor_status")}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={() => {
              setDraft({});
              setLabels({});
              onReset();
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" /> Reset Filters
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={() => {
                const dateError = firstDateRangeError(
                  DATE_RANGES.map((r): DateRangeTriple => [draft[`${r.param}_from`], draft[`${r.param}_to`], r.label]),
                );
                if (dateError) {
                  toast.error(dateError);
                  return;
                }
                const cleaned: DesignFilters = {};
                Object.entries(draft).forEach(([k, v]) => {
                  const value = typeof v === "string" ? v.trim() : v;
                  if (value) cleaned[k] = value;
                });
                onApply(cleaned, labels);
                onClose();
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" /> Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Bulk assign (report maker / validator) ──────────────────────────────────
type AssignKind = "report_maker" | "validator";

interface AssignModalState {
  kind: AssignKind;
  projectId: number;
  projectName: string;
  siteIds: number[];
}

// What every bulk endpoint answers with.
interface BulkResult {
  updated_count: number;
  updated_site_ids: number[];
  skipped_count: number;
  skipped: Array<{ id: number; site_code?: string; reason: string }>;
}

interface AssignModalProps {
  state: AssignModalState | null;
  onClose: () => void;
  onDone: (result: BulkResult) => void;
}

const DesignBulkAssignModal: React.FC<AssignModalProps> = ({ state, onClose, onDone }) => {
  const [userOpt, setUserOpt] = useState<SelectOption | null>(null);
  const [priority, setPriority] = useState("");
  const [remarks, setRemarks] = useState("");
  const [work, setWork] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (state) {
      setUserOpt(null);
      setPriority("");
      setRemarks("");
      setWork("");
      setError("");
    }
  }, [state]);

  if (!state) return null;

  const noun = state.kind === "report_maker" ? "Report Maker" : "Validator";

  const submit = async () => {
    if (!userOpt) {
      setError(`Please select a ${noun.toLowerCase()}.`);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/designing/sites/bulk/assign", {
        site_ids: state.siteIds,
        kind: state.kind,
        user_id: userOpt.id,
        ...(state.kind === "report_maker" && work.trim() ? { work_description: work.trim() } : {}),
        priority: priority || undefined,
        remarks: remarks.trim() || undefined,
      });
      onDone(res.data?.data);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Assign {noun}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="px-3 py-2 rounded border text-xs bg-blue-50 border-blue-200 text-blue-800">
            {state.siteIds.length} site(s) of {state.projectName || "the selected project"} will be assigned. Only users on
            that project who may act as {noun.toLowerCase()} are listed.
            {state.kind === "report_maker" && " The report maker is added to each site — report makers already on a site stay."}
          </div>
          <div>
            <label className={labelClass}>{noun} <span className="text-red-500">*</span></label>
            <SelectWithSearch
              placeholder={`Select ${noun.toLowerCase()}...`}
              apiEndpoint="/designing/assignable-users"
              dataKey="users"
              extraParams={{ kind: state.kind, project_id: state.projectId }}
              displayFormat={userOptionLabel}
              minSearchLength={0}
              value={userOpt}
              onChange={setUserOpt}
            />
          </div>
          {state.kind === "report_maker" && (
            <div>
              <label className={labelClass}>Work (optional)</label>
              <input
                type="text"
                className={inputClass}
                value={work}
                maxLength={2000}
                onChange={(e) => setWork(e.target.value)}
                placeholder="What will this report maker do on these sites?"
              />
            </div>
          )}
          <div>
            <label className={labelClass}>Priority</label>
            <select className={inputClass} value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="">Not set</option>
              {DESIGN_PRIORITIES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Remarks</label>
            <textarea
              placeholder="Remarks (optional)..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
            />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">{error}</div>}
        </div>
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting} className="flex-1 px-4 py-2 text-sm font-medium text-white rounded disabled:opacity-60 bg-blue-800 hover:bg-blue-900">
            {submitting ? "Assigning..." : state.kind === "report_maker" ? "Add Report Maker" : `Assign ${noun}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Skipped rows of a bulk action ───────────────────────────────────────────
const DesignSkippedModal: React.FC<{ result: BulkResult | null; onClose: () => void }> = ({ result, onClose }) => {
  if (!result) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Some sites were skipped</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="px-3 py-2 rounded border text-xs bg-yellow-50 border-yellow-200 text-yellow-800">
            {result.updated_count} site(s) updated, {result.skipped_count} skipped.
          </div>
          <div className="max-h-72 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-200">
            {(result.skipped || []).map((row) => (
              <div key={row.id} className="px-3 py-2">
                <p className="text-sm font-medium text-gray-900">{row.site_code || `Site #${row.id}`}</p>
                <p className="text-xs text-gray-600 mt-0.5">{row.reason}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end p-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Cells ───────────────────────────────────────────────────────────────────
const thClass = "px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap";
const tdClass = "px-4 py-4 whitespace-nowrap text-sm text-gray-700";
const badgeClass = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";

const Truncated: React.FC<{ text?: string | null; wide?: boolean }> = ({ text, wide }) => (
  <span className={`truncate block text-sm text-gray-700 ${wide ? "max-w-[220px]" : "max-w-[160px]"}`} title={text || ""}>
    {text || "-"}
  </span>
);

// Label / value lines of the two compact commercial cells; empty lines are dropped.
const CompactLines: React.FC<{ lines: Array<[label: string, value: any]>; remarks?: string | null }> = ({ lines, remarks }) => {
  const filled = lines.filter(([, value]) => value !== null && value !== undefined && value !== "" && value !== "-");
  if (filled.length === 0 && !remarks) return <span className="text-sm text-gray-400">-</span>;
  return (
    <div className="text-xs text-gray-700 space-y-0.5">
      {filled.map(([label, value]) => (
        <div key={label} className="whitespace-nowrap">
          <span className="text-gray-500">{label}:</span> {value}
        </div>
      ))}
      {remarks && (
        <div className="truncate max-w-[220px] text-gray-500" title={remarks}>
          {remarks}
        </div>
      )}
    </div>
  );
};

// One exported row — the table's columns, in the table's order.
const toExportRow = (s: any, access: CommercialAccess): Record<string, any> => {
  const row: Record<string, any> = {
    "Site ID": s.site_code,
    "Reference ID": s.reference_id || "",
    "Site Name": s.site_name || "",
    "Project Name": s.project?.project_name || "",
    Activity: s.activity?.activity_name || "",
    Client: s.client?.client_full_name || s.client?.client_short_name || "",
    "Report Type": s.report_type ? reportTypeLabel(s.report_type) : "",
    Billable: s.is_billable ? "Yes" : "No",
    "Report Instruction 1": s.report_instruction1 || "",
    "Report Instruction 2": s.report_instruction2 || "",
    Priority: s.priority ? priorityLabel(s.priority) : "",
    "Required Date": s.required_date || "",
    "PO Number": s.po_number || "",
    "PO Date": s.po_date || "",
    "Work Completion Status": s.status ? workStatusLabel(s.status) : "",
    "Site Stage": s.workflow_status ? stageLabel(s.workflow_status) : "",
    Cycle: s.cycle_number ?? "",
    "Report Maker": (s.report_makers || []).length
      ? s.report_makers.map((m: any) => personName(m.user)).join(", ")
      : s.report_maker
        ? personName(s.report_maker)
        : "",
    "Report Maker Work": (s.report_makers || [])
      .filter((m: any) => m.work_description)
      .map((m: any) => `${personName(m.user)}: ${m.work_description}`)
      .join("; "),
    Validator: s.validator ? personName(s.validator) : "",
  };
  DESIGN_STAGE_DATE_COLUMNS.forEach((c) => {
    row[c.label] = s[c.key] ? formatDateTime(s[c.key]) : "";
  });
  row["Last Updated Date"] = s.updated_at ? formatDateTime(s.updated_at) : "";
  if (access.billing) {
    row["Billing Status"] = s.billing_status ? billingStatusLabel(s.billing_status) : "";
    row["Billing Invoice Number"] = s.billing_invoice_number || "";
    row["Billing Invoice Date"] = s.billing_invoice_date || "";
    row["Billing Invoice Amount"] = s.billing_invoice_amount ?? "";
    row["Billing Details"] = s.billing_details || "";
  }
  if (access.vendor) {
    row["Vendor"] = s.vendor?.vendor_full_name || s.vendor?.vendor_short_name || "";
    row["Vendor Status"] = s.vendor_status ? vendorStatusLabel(s.vendor_status) : "";
    row["Vendor PO Number"] = s.vendor_po_number || "";
    row["Vendor PO Date"] = s.vendor_po_date || "";
    row["Vendor Invoice Number"] = s.vendor_invoice_number || "";
    row["Vendor Invoice Date"] = s.vendor_invoice_date || "";
    row["Vendor Invoice Amount"] = s.vendor_invoice_amount ?? "";
    row["Vendor Invoice Details"] = s.vendor_invoice_details || "";
  }
  return row;
};

// ── Main list ───────────────────────────────────────────────────────────────
interface DesignSitesListProps {
  // "all" = Project Sites (GET /designing/sites/all), "my" = My Sites (GET /designing/sites).
  scope: Scope;
  title: string;
}

const DesignSitesList: React.FC<DesignSitesListProps> = ({ scope, title }) => {
  const { user, hasPermission } = useAuth();
  const { confirm, prompt } = useConfirm();
  const endpoint = scope === "all" ? "/designing/sites/all" : "/designing/sites";

  const [sites, setSites] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationType | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [stageCounts, setStageCounts] = useState<Record<string, number>>({});
  // Decided by the server (DesignSite.view_commercials); the rows of anyone
  // else simply do not carry the billing / vendor fields.
  const [commercialAccess, setCommercialAccess] = useState<CommercialAccess>({ billing: false, vendor: false });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [filters, setFilters] = useState<DesignFilters>({});
  const [filterLabels, setFilterLabels] = useState<Record<string, string>>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  // Selected rows by id, kept as the row itself so the bulk rules (stage,
  // project) can be checked across pages.
  const [selected, setSelected] = useState<Record<number, any>>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [assignModal, setAssignModal] = useState<AssignModalState | null>(null);
  const [skippedResult, setSkippedResult] = useState<BulkResult | null>(null);

  const [showExport, setShowExport] = useState(false);
  const [exportData, setExportData] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);

  const can = (action: string) => hasPermission(DESIGN_RESOURCE, action);
  const canCreate = can(DESIGN_ACTIONS.CREATE);
  const canUpload = can(DESIGN_ACTIONS.UPLOAD);
  const canExport = can(DESIGN_ACTIONS.EXPORT);
  const canView = can(DESIGN_ACTIONS.VIEW);
  const canEdit = can(DESIGN_ACTIONS.EDIT);
  const canHold = can(DESIGN_ACTIONS.HOLD);
  const canResume = can(DESIGN_ACTIONS.RESUME);
  const canCancel = can(DESIGN_ACTIONS.CANCEL);
  const canBulkCancel = can(DESIGN_ACTIONS.BULK_CANCEL);
  const canSendToClient = can(DESIGN_ACTIONS.SEND_TO_CLIENT);
  const canAssignReportMaker = can(DESIGN_ACTIONS.ASSIGN_REPORT_MAKER);
  const canAssignValidator = can(DESIGN_ACTIONS.ASSIGN_VALIDATOR);
  const hasBulk = canBulkCancel || canSendToClient || canAssignReportMaker || canAssignValidator;

  const projectParams = useMemo(() => designProjectParams(user?.role?.level), [user?.role?.level]);

  const debouncedSearchUpdate = useDebouncedCallback((val: string) => {
    setDebouncedSearch(val);
    setPage(1);
  }, 400);

  const activeFiltersCount = useMemo(
    () => Object.values(filters).filter((v) => v !== undefined && v !== "").length,
    [filters],
  );

  const buildParams = useCallback((): Record<string, any> => {
    const params: Record<string, any> = { sort_by: sortBy, sort_order: sortOrder };
    if (debouncedSearch) params.search = debouncedSearch;
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== "") params[k] = v;
    });
    return params;
  }, [sortBy, sortOrder, debouncedSearch, filters]);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoint, { params: { ...buildParams(), page, limit } });
      const body = res.data?.data ?? {};
      const rows: any[] = Array.isArray(body.data) ? body.data : [];
      setSites(rows);
      setStageCounts(body.stage_counts || {});
      const nextAccess = accessFromBody(body);
      setCommercialAccess((prev) =>
        prev.billing === nextAccess.billing && prev.vendor === nextAccess.vendor ? prev : nextAccess,
      );
      // Keep the selected rows current, so a stage that moved is judged as it is now.
      setSelected((prev) => {
        if (Object.keys(prev).length === 0) return prev;
        const next = { ...prev };
        rows.forEach((row) => {
          if (next[row.id]) next[row.id] = row;
        });
        return next;
      });
      const pg = body.pagination;
      setPagination(
        pg
          ? {
              offset: (Number(pg.current_page || 1) - 1) * Number(pg.per_page || limit),
              limit: Number(pg.per_page ?? limit),
              page: Number(pg.current_page ?? 1),
              total_pages: Number(pg.total_pages ?? 1),
              total_count: Number(pg.total ?? rows.length),
              has_next_page: !!pg.has_next_page,
              has_prev_page: !!pg.has_prev_page,
              next_page: pg.next_page ?? null,
              prev_page: pg.prev_page ?? null,
            }
          : null,
      );
    } catch (e: any) {
      // e.g. the 400 the server answers for a date range over the limit.
      toast.error(e?.response?.data?.message || "Failed to fetch sites");
    } finally {
      setLoading(false);
    }
  }, [endpoint, buildParams, page, limit]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(field);
      setSortOrder("DESC");
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <FaSort className="w-3 h-3 text-gray-400" />;
    return sortOrder === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-800" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-800" />
    );
  };

  const plainTh = (label: string, minWidth = "min-w-[140px]") => (
    <th key={label} className={`${thClass} ${minWidth}`}>{label}</th>
  );

  const sortTh = (field: string, label: string, minWidth = "min-w-[140px]") => (
    <th key={field} className={`${thClass} ${minWidth}`}>
      <button type="button" onClick={() => handleSort(field)} className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold whitespace-nowrap">
        {label} {getSortIcon(field)}
      </button>
    </th>
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const baseParams = { ...buildParams(), limit: 100 };
      let all: any[] = [];
      let commercials: CommercialAccess = { billing: false, vendor: false };
      let pageNum = 1;
      let totalPages = 1;
      do {
        const res = await api.get(endpoint, { params: { ...baseParams, page: pageNum } });
        const body = res.data?.data ?? {};
        all = all.concat(Array.isArray(body.data) ? body.data : []);
        commercials = accessFromBody(body);
        totalPages = body.pagination ? Number(body.pagination.total_pages || 1) : 1;
        pageNum++;
      } while (pageNum <= totalPages && pageNum <= 100);

      setExportData(all.map((s) => toExportRow(s, commercials)));
      setShowExport(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to prepare export");
    } finally {
      setExporting(false);
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const removeFilter = (key: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPage(1);
  };

  const clearAllFilters = () => {
    setFilters({});
    setFilterLabels({});
    setPage(1);
  };

  // A stage chip sets the Site Stage filter; clicking the active one clears it.
  const toggleStage = (stage: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (next.workflow_status === stage) delete next.workflow_status;
      else next.workflow_status = stage;
      return next;
    });
    setPage(1);
  };

  const chipText = (key: string, value: string) =>
    `${FILTER_LABELS[key] || key.replace(/_/g, " ")}: ${filterValueLabel(key, value, filterLabels)}`;

  // ── Row actions ───────────────────────────────────────────────────────────
  // The axios instance toasts the server's message for a mutating call, success
  // or failure, so nothing is toasted again here.
  const runRowAction = async (siteId: number, request: () => Promise<any>) => {
    setBusyId(siteId);
    try {
      await request();
      fetchSites();
    } catch {
      // already toasted
    } finally {
      setBusyId(null);
    }
  };

  const handleHold = async (site: any) => {
    const reason = await prompt({
      title: `Hold Site: ${site.site_code}`,
      message: "The site will be put on hold and can be resumed later.",
      label: "Reason for hold",
      placeholder: "Reason for hold...",
      required: true,
      multiline: true,
      variant: "warning",
      confirmText: "Hold Site",
    });
    if (reason === null || !reason.trim()) return;
    await runRowAction(site.id, () => api.put(`/designing/sites/${site.id}/hold`, { hold_reason: reason.trim() }));
  };

  const handleResume = async (site: any) => {
    const ok = await confirm({
      title: `Resume Site: ${site.site_code}`,
      message: "The site will resume from the stage it was held at.",
      confirmText: "Resume Site",
      variant: "info",
    });
    if (!ok) return;
    await runRowAction(site.id, () => api.put(`/designing/sites/${site.id}/resume`, {}));
  };

  const handleCancel = async (site: any) => {
    const reason = await prompt({
      title: `Cancel Site: ${site.site_code}`,
      message: "This will cancel the site. A cancelled site cannot be edited or worked on.",
      label: "Reason for cancellation",
      placeholder: "Reason for cancellation...",
      required: true,
      multiline: true,
      variant: "danger",
      confirmText: "Cancel Site",
      cancelText: "Keep Site",
    });
    if (reason === null || !reason.trim()) return;
    await runRowAction(site.id, () => api.put(`/designing/sites/${site.id}/cancel`, { cancel_reason: reason.trim() }));
  };

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const bulk = useMemo(() => {
    const rows = Object.values(selected);
    const projectIds = Array.from(new Set(rows.map((r) => r.project?.id ?? r.project_id)));
    const oneProject = projectIds.length === 1 && projectIds[0] != null;
    const allAt = (stages: string[]) => rows.every((r) => stages.includes(r.workflow_status));
    const stageNames = (stages: string[]) => stages.map((s) => stageLabel(s)).join(" or ");
    // Why an assign button is disabled, or "" when it is not.
    const assignBlock = (stages: string[]) =>
      !allAt(stages)
        ? `Every selected site must be at ${stageNames(stages)}`
        : !oneProject
          ? "Every selected site must belong to the same project"
          : "";
    return {
      count: rows.length,
      ids: rows.map((r) => Number(r.id)),
      projectId: oneProject ? Number(projectIds[0]) : 0,
      projectName: oneProject ? rows[0]?.project?.project_name || "" : "",
      sendBlock: allAt(["report_finalized"]) ? "" : `Every selected site must be at ${stageNames(["report_finalized"])}`,
      reportMakerBlock: assignBlock(["site_data_uploaded", "report_maker_assigned"]),
      validatorBlock: assignBlock(["report_completed", "validator_assigned"]),
    };
  }, [selected]);

  const allOnPageSelected = sites.length > 0 && sites.every((s) => selected[s.id]);

  const toggleRow = (site: any) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[site.id]) delete next[site.id];
      else next[site.id] = site;
      return next;
    });

  const togglePage = () =>
    setSelected((prev) => {
      const next = { ...prev };
      sites.forEach((s) => {
        if (allOnPageSelected) delete next[s.id];
        else next[s.id] = s;
      });
      return next;
    });

  // Every bulk endpoint names the rows it left out; the count itself has
  // already been toasted from the server's message by the axios instance.
  const finishBulk = (result?: BulkResult) => {
    if (result && Number(result.skipped_count) > 0) setSkippedResult(result);
    setSelected({});
    fetchSites();
  };

  const runBulk = async (request: () => Promise<any>) => {
    setBulkBusy(true);
    try {
      const res = await request();
      finishBulk(res.data?.data);
    } catch {
      // already toasted
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkCancel = async () => {
    const reason = await prompt({
      title: "Cancel Selected Sites",
      message: `This will cancel ${bulk.count} selected site(s).`,
      warning: "Sites that are already cancelled, or whose report has been sent to the client, are skipped.",
      label: "Reason for cancellation",
      placeholder: "Reason for cancellation...",
      required: true,
      multiline: true,
      variant: "danger",
      confirmText: "Cancel Sites",
      cancelText: "Keep Sites",
    });
    if (reason === null || !reason.trim()) return;
    await runBulk(() => api.post("/designing/sites/bulk/cancel", { site_ids: bulk.ids, cancel_reason: reason.trim() }));
  };

  const handleBulkSendToClient = async () => {
    const ok = await confirm({
      title: "Mark Report Sent to Client",
      message: `Mark the report of ${bulk.count} selected site(s) as sent to the client?`,
      warning: "This completes the sites.",
      confirmText: "Mark as Sent",
      variant: "info",
    });
    if (!ok) return;
    await runBulk(() => api.post("/designing/sites/bulk/send-to-client", { site_ids: bulk.ids }));
  };

  const openAssign = (kind: AssignKind) =>
    setAssignModal({ kind, projectId: bulk.projectId, projectName: bulk.projectName, siteIds: bulk.ids });

  const bulkButtonClass =
    "px-3 py-1.5 rounded flex items-center gap-2 text-xs font-medium text-white transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed";

  const colSpan = (hasBulk ? 1 : 0) + 20 + DESIGN_STAGE_DATE_COLUMNS.length + (commercialAccess.billing ? 2 : 0) + (commercialAccess.vendor ? 2 : 0);

  return (
    <div className="">
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-600 mt-1">
                {scope === "all"
                  ? "Designing sites of your projects — track report preparation stage by stage"
                  : "Designing sites you created, or are the report maker or validator of"}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {canExport && (
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
                >
                  <FaFileExcel className="w-4 h-4" />
                  {exporting ? "Preparing..." : "Export Excel"}
                </button>
              )}
              {canUpload && (
                <Link
                  to="/designing/upload"
                  className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
                >
                  <FaUpload className="w-4 h-4" /> Multiple Site Creation
                </Link>
              )}
              {canCreate && (
                <Link
                  to="/designing/create"
                  className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
                >
                  <FaPlus className="w-4 h-4" /> Single Site Creation
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 max-w-md">
              <label className="block text-xs font-medium text-gray-700 mb-1">Search Sites</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by site ID, name, reference ID, PO number or project"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    debouncedSearchUpdate(e.target.value);
                  }}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
            <div className="w-full sm:w-auto">
              <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
              >
                <FiFilter className="w-4 h-4" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Active filter chips */}
          {activeFiltersCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                {Object.entries(filters).map(([key, value]) =>
                  value ? (
                    <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {chipText(key, value)}
                      <button onClick={() => removeFilter(key)} className="ml-1 text-blue-600 hover:text-blue-800">
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  ) : null,
                )}
                <button onClick={clearAllFilters} className="text-sm text-gray-500 hover:text-gray-700 underline">
                  Clear all
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Stage chips — how much live work is sitting at each stage (not narrowed by the filters) */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 flex items-center gap-2 flex-wrap">
          {DESIGN_STAGES.map((stage) => {
            const active = filters.workflow_status === stage.value;
            return (
              <button
                key={stage.value}
                type="button"
                onClick={() => toggleStage(stage.value)}
                title={active ? "Clear the Site Stage filter" : `Show sites at ${stage.label}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? "bg-blue-800 text-white border-blue-800"
                    : `${stageBadgeClass(stage.value)} border-transparent hover:border-gray-400`
                }`}
              >
                {stage.label}
                <span className={`px-1.5 rounded-full font-semibold ${active ? "bg-white text-blue-800" : "bg-white bg-opacity-70"}`}>
                  {stageCounts[stage.value] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Bulk action bar */}
        {hasBulk && bulk.count > 0 && (
          <div className="px-4 sm:px-6 py-3 border-b border-blue-200 bg-blue-50 flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-blue-900 mr-2">{bulk.count} selected</span>
            {canAssignReportMaker && (
              <button
                onClick={() => openAssign("report_maker")}
                disabled={bulkBusy || !!bulk.reportMakerBlock}
                title={bulk.reportMakerBlock || "Add a report maker to the selected sites"}
                className={`${bulkButtonClass} bg-blue-800 hover:bg-blue-900`}
              >
                <FaUserEdit className="w-3 h-3" /> Add Report Maker
              </button>
            )}
            {canAssignValidator && (
              <button
                onClick={() => openAssign("validator")}
                disabled={bulkBusy || !!bulk.validatorBlock}
                title={bulk.validatorBlock || "Assign a validator to the selected sites"}
                className={`${bulkButtonClass} bg-blue-800 hover:bg-blue-900`}
              >
                <FaUserCheck className="w-3 h-3" /> Assign Validator
              </button>
            )}
            {canSendToClient && (
              <button
                onClick={handleBulkSendToClient}
                disabled={bulkBusy || !!bulk.sendBlock}
                title={bulk.sendBlock || "Mark the report of the selected sites as sent to the client"}
                className={`${bulkButtonClass} bg-green-600 hover:bg-green-700`}
              >
                <FaPaperPlane className="w-3 h-3" /> Mark Report Sent to Client
              </button>
            )}
            {canBulkCancel && (
              <button
                onClick={handleBulkCancel}
                disabled={bulkBusy}
                title="Cancel the selected sites"
                className={`${bulkButtonClass} bg-red-600 hover:bg-red-700`}
              >
                <FaBan className="w-3 h-3" /> Bulk Cancel
              </button>
            )}
            <button onClick={() => setSelected({})} className="ml-auto text-sm text-gray-500 hover:text-gray-700 underline">
              Clear selection
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {hasBulk && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={togglePage}
                      disabled={sites.length === 0}
                      title="Select every site on this page"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                )}
                {plainTh("Actions", "min-w-[180px]")}
                {sortTh("site_code", "Site ID", "min-w-[120px]")}
                {plainTh("Reference ID", "min-w-[130px]")}
                {sortTh("site_name", "Site Name", "min-w-[160px]")}
                {plainTh("Project Name", "min-w-[160px]")}
                {plainTh("Activity")}
                {plainTh("Client", "min-w-[160px]")}
                {plainTh("Report Type", "min-w-[110px]")}
                {plainTh("Billable", "min-w-[90px]")}
                {plainTh("Report Instruction 1", "min-w-[200px]")}
                {plainTh("Report Instruction 2", "min-w-[200px]")}
                {sortTh("priority", "Priority", "min-w-[100px]")}
                {sortTh("required_date", "Required Date")}
                {plainTh("PO Number", "min-w-[120px]")}
                {sortTh("po_date", "PO Date", "min-w-[120px]")}
                {sortTh("status", "Work Completion Status", "min-w-[190px]")}
                {sortTh("workflow_status", "Site Stage", "min-w-[190px]")}
                {plainTh("Report Maker", "min-w-[150px]")}
                {plainTh("Validator", "min-w-[150px]")}
                {DESIGN_STAGE_DATE_COLUMNS.map((c) => sortTh(c.key, c.label, "min-w-[170px]"))}
                {sortTh("updated_at", "Last Updated Date", "min-w-[170px]")}
                {commercialAccess.billing && (
                  <>
                    {plainTh("Billing Status")}
                    {plainTh("Billing Details", "min-w-[200px]")}
                  </>
                )}
                {commercialAccess.vendor && (
                  <>
                    {plainTh("Vendor Status", "min-w-[170px]")}
                    {plainTh("Vendor Invoice Details", "min-w-[220px]")}
                  </>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 sm:px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <span className="text-sm text-gray-600">Loading sites...</span>
                    </div>
                  </td>
                </tr>
              ) : sites.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 sm:px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <FaDraftingCompass className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No sites found</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sites.map((site) => {
                  const busy = busyId === site.id;
                  return (
                    <tr key={site.id} className={`transition-colors ${selected[site.id] ? "bg-blue-50" : "hover:bg-gray-50"}`}>
                      {hasBulk && (
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={!!selected[site.id]}
                            onChange={() => toggleRow(site)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 flex-wrap">
                          {canView && (
                            <Link to={`/designing/sites/${site.id}`} className="p-2 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-800 transition-colors" title="View">
                              <LuEye className="w-4 h-4" />
                            </Link>
                          )}
                          {canEdit && site.status !== "cancelled" && (
                            <Link to={`/designing/${site.id}/update`} className="p-2 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-800 transition-colors" title="Edit">
                              <FaEdit className="w-4 h-4" />
                            </Link>
                          )}
                          {canResume && site.status === "hold" && (
                            <button onClick={() => handleResume(site)} disabled={busy} className="p-2 rounded hover:bg-green-50 text-green-600 hover:text-green-800 transition-colors disabled:opacity-50" title="Resume">
                              <FaPlay className="w-4 h-4" />
                            </button>
                          )}
                          {canHold && (site.status === "in_progress" || site.status === "reopened") && (
                            <button onClick={() => handleHold(site)} disabled={busy} className="p-2 rounded hover:bg-amber-50 text-amber-600 hover:text-amber-800 transition-colors disabled:opacity-50" title="Hold">
                              <FaPause className="w-4 h-4" />
                            </button>
                          )}
                          {canCancel && site.status !== "completed" && site.status !== "cancelled" && (
                            <button onClick={() => handleCancel(site)} disabled={busy} className="p-2 rounded hover:bg-red-50 text-red-600 hover:text-red-800 transition-colors disabled:opacity-50" title="Cancel">
                              <FaBan className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{site.site_code}</td>
                      <td className={tdClass}>{site.reference_id || "-"}</td>
                      <td className="px-4 py-4"><Truncated text={site.site_name} /></td>
                      <td className="px-4 py-4"><Truncated text={site.project?.project_name} /></td>
                      <td className={tdClass}>{site.activity?.activity_name || "-"}</td>
                      <td className="px-4 py-4"><Truncated text={site.client?.client_full_name || site.client?.client_short_name} /></td>
                      <td className={tdClass}>{reportTypeLabel(site.report_type)}</td>
                      <td className={tdClass}>{site.is_billable ? "Yes" : "No"}</td>
                      <td className="px-4 py-4"><Truncated text={site.report_instruction1} wide /></td>
                      <td className="px-4 py-4"><Truncated text={site.report_instruction2} wide /></td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`${badgeClass} ${priorityBadgeClass(site.priority)}`}>{priorityLabel(site.priority)}</span>
                      </td>
                      <td className={tdClass}>{formatDateOnly(site.required_date)}</td>
                      <td className={tdClass}>{site.po_number || "-"}</td>
                      <td className={tdClass}>{formatDateOnly(site.po_date)}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`${badgeClass} ${workStatusBadgeClass(site.status)}`}>{workStatusLabel(site.status)}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`${badgeClass} ${stageBadgeClass(site.workflow_status)}`}>{stageLabel(site.workflow_status)}</span>
                        {Number(site.cycle_number) > 1 && <div className="text-xs text-gray-500 mt-1">Cycle {site.cycle_number}</div>}
                      </td>
                      <td className={tdClass}>
                        {(site.report_makers || []).length > 1 ? (
                          <span
                            title={site.report_makers
                              .map(
                                (m: any) =>
                                  `${personName(m.user)} — ${reportMakerStatusLabel(m.report_status)}${m.work_description ? ` — ${m.work_description}` : ""}`,
                              )
                              .join("\n")}
                          >
                            {personName(site.report_makers[0].user)}{" "}
                            <span className="text-xs text-gray-500">+{site.report_makers.length - 1}</span>
                          </span>
                        ) : (
                          personName(site.report_makers?.[0]?.user || site.report_maker)
                        )}
                        {(site.report_makers || []).length > 0 &&
                          ["report_maker_assigned", "report_completed", "validator_assigned"].includes(site.workflow_status) &&
                          (() => {
                            const p = teamProgress(site.report_makers);
                            const done = site.workflow_status === "validator_assigned" ? p.accepted : p.submitted + p.accepted;
                            return (
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                {done}/{p.total} {site.workflow_status === "validator_assigned" ? "accepted" : "submitted"}
                                {p.rejected ? <span className="text-red-600"> · {p.rejected} sent back</span> : null}
                              </div>
                            );
                          })()}
                      </td>
                      <td className={tdClass}>{personName(site.validator)}</td>
                      {DESIGN_STAGE_DATE_COLUMNS.map((c) => (
                        <td key={c.key} className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDateTime(site[c.key])}</td>
                      ))}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDateTime(site.updated_at)}</td>
                      {commercialAccess.billing && (
                        <>
                          <td className={tdClass}>{billingStatusLabel(site.billing_status)}</td>
                          <td className="px-4 py-4">
                            <CompactLines
                              lines={[
                                ["Invoice No", site.billing_invoice_number],
                                ["Invoice Date", formatDateOnly(site.billing_invoice_date)],
                                ["Amount", formatAmount(site.billing_invoice_amount)],
                              ]}
                              remarks={site.billing_details}
                            />
                          </td>
                        </>
                      )}
                      {commercialAccess.vendor && (
                        <>
                          <td className={tdClass}>{vendorStatusLabel(site.vendor_status)}</td>
                          <td className="px-4 py-4">
                            <CompactLines
                              lines={[
                                ["Vendor", site.vendor?.vendor_full_name || site.vendor?.vendor_short_name],
                                ["PO No", site.vendor_po_number],
                                ["PO Date", formatDateOnly(site.vendor_po_date)],
                                ["Invoice No", site.vendor_invoice_number],
                                ["Invoice Date", formatDateOnly(site.vendor_invoice_date)],
                                ["Amount", formatAmount(site.vendor_invoice_amount)],
                              ]}
                              remarks={site.vendor_invoice_details}
                            />
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <Pagination pagination={pagination} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
        )}
      </div>

      {/* Filter modal */}
      <DesignFilterModal
        isOpen={isFilterModalOpen}
        scope={scope}
        commercialAccess={commercialAccess}
        projectParams={projectParams}
        initial={filters}
        initialLabels={filterLabels}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={(f, labels) => {
          setFilters(f);
          setFilterLabels(labels);
          setPage(1);
        }}
        onReset={clearAllFilters}
      />

      {/* Bulk assign modal */}
      <DesignBulkAssignModal state={assignModal} onClose={() => setAssignModal(null)} onDone={finishBulk} />

      {/* Skipped rows of the last bulk action */}
      <DesignSkippedModal result={skippedResult} onClose={() => setSkippedResult(null)} />

      {/* Export modal */}
      {showExport && (
        <ExcelDownload
          isOpen={showExport}
          data={exportData}
          filename={scope === "all" ? "designing_project_sites" : "designing_my_sites"}
          title="Export Designing Sites"
          description="Download Designing site details"
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
};

export default DesignSitesList;
